import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import MathText from '../components/MathText';

interface QuestionDetails {
  id: number;
  question: string;
  option1: string;
  option2: string;
  option3: string;
  option4: string;
  correct_option: number;
  difficulty_level: string;
  marks: number;
  explanation?: string;
  board_id: number;
  board_name: string;
  standard_id: number;
  standard_name: string;
  medium_id?: number;
  medium_name?: string;
  medium_code?: string;
  stream_id?: number;
  stream_name?: string;
  subject_id: number;
  subject_name: string;
  chapter_id: number;
  chapter_name: string;
  topic_id?: number;
  topic_name?: string;
  subtopic_id?: number;
  subtopic_name?: string;
  page_number?: number;
  status: string;
  verification_notes?: string;
  created_by_name?: string;
  created_at: string;
  updated_at?: string;
  verified_by_name?: string;
  verified_at?: string;
}

interface QuestionReport {
  id: number;
  student_name?: string;
  parent_name?: string;
  report_reason: string;
  report_description?: string;
  reported_at: string;
  status: string;
  admin_notes?: string;
  reviewed_by_name?: string;
  reviewed_at?: string;
}

const QuestionView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [question, setQuestion] = useState<QuestionDetails | null>(null);
  const [reports, setReports] = useState<QuestionReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      loadQuestion();
      loadReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadQuestion = async () => {
    try {
      setLoading(true);
      const response = await api.questions.getById(parseInt(id!));
      setQuestion(response.data.question);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load question details');
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      const response = await api.questionReports.getByQuestionId(parseInt(id!));
      setReports(response.data || []);
    } catch (err) {
      console.error('Failed to load reports:', err);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'success';
      case 'medium':
        return 'warning';
      case 'hard':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getReasonLabel = (reason: string) => {
    const labels: Record<string, string> = {
      wrong_answer: 'Wrong Answer',
      typo: 'Typo',
      incorrect_options: 'Incorrect Options',
      unclear_question: 'Unclear Question',
      missing_information: 'Missing Info',
      other: 'Other',
    };
    return labels[reason] || reason;
  };

  const getReportStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'under_review':
        return 'info';
      case 'resolved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !question) {
    return (
      <Box>
        <Alert severity="error">{error || 'Question not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/questions')} sx={{ mt: 2 }}>
          Back to Questions
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/questions')}>
            Back
          </Button>
          <Typography variant="h4" fontWeight="bold">
            Question Details
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/questions/${id}/edit`)}
        >
          Edit Question
        </Button>
      </Box>

      {/* Status and Metadata Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <Chip
                label={question.status}
                color={getStatusColor(question.status) as any}
                icon={question.status === 'approved' ? <CheckCircleIcon /> : undefined}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Difficulty
              </Typography>
              <Chip
                label={question.difficulty_level}
                color={getDifficultyColor(question.difficulty_level) as any}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Marks
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {question.marks}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Medium
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {question.medium_name || '-'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Question Content */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Question
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
          <MathText text={question.question} />
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Options
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2, 3, 4].map((optionNum) => {
            const optionKey = `option${optionNum}` as keyof QuestionDetails;
            const optionText = question[optionKey] as string;
            const isCorrect = question.correct_option === optionNum;

            return (
              <Box
                key={optionNum}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  bgcolor: isCorrect ? 'success.50' : 'grey.50',
                  borderRadius: 1,
                  border: isCorrect ? '2px solid' : '1px solid',
                  borderColor: isCorrect ? 'success.main' : 'grey.300',
                }}
              >
                <Typography variant="body1" fontWeight={isCorrect ? 'bold' : 'normal'}>
                  {optionNum}.
                </Typography>
                <Typography variant="body1" sx={{ flexGrow: 1 }}>
                  <MathText text={optionText || ''} />
                </Typography>
                {isCorrect && (
                  <Chip
                    label="Correct Answer"
                    color="success"
                    size="small"
                    icon={<CheckCircleIcon />}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        {question.explanation && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Explanation
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              <MathText text={question.explanation || ''} />
            </Typography>
          </>
        )}
      </Paper>

      {/* Academic Metadata */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Academic Information
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Board
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {question.board_name}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Standard
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {question.standard_name}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Medium
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {question.medium_name || '-'}
            </Typography>
          </Grid>
          {question.stream_name && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Stream
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {question.stream_name}
              </Typography>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Subject
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {question.subject_name}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Chapter
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {question.chapter_name}
            </Typography>
          </Grid>
          {question.topic_name && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Topic
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {question.topic_name}
              </Typography>
            </Grid>
          )}
          {question.subtopic_name && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Subtopic
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {question.subtopic_name}
              </Typography>
            </Grid>
          )}
          {question.page_number && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Page Number
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {question.page_number}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Verification Information */}
      {(question.verification_notes || question.verified_by_name) && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Verification Details
          </Typography>
          <Grid container spacing={2}>
            {question.verified_by_name && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Verified By
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {question.verified_by_name}
                </Typography>
              </Grid>
            )}
            {question.verified_at && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Verified At
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {new Date(question.verified_at).toLocaleString('en-IN')}
                </Typography>
              </Grid>
            )}
            {question.verification_notes && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">
                  Notes
                </Typography>
                <Typography variant="body1">{question.verification_notes}</Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Question Reports */}
      {reports.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FlagIcon color="error" />
            <Typography variant="h6" fontWeight="bold">
              Question Reports ({reports.length})
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Reported By</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reported At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>{report.student_name || report.parent_name || 'Unknown'}</TableCell>
                    <TableCell>
                      <Chip label={getReasonLabel(report.report_reason)} size="small" />
                    </TableCell>
                    <TableCell>{report.report_description || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={report.status.replace('_', ' ')}
                        color={getReportStatusColor(report.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(report.reported_at).toLocaleDateString('en-IN')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Metadata Footer */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Metadata
        </Typography>
        <Grid container spacing={2}>
          {question.created_by_name && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Created By
              </Typography>
              <Typography variant="body1">{question.created_by_name}</Typography>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Created At
            </Typography>
            <Typography variant="body1">
              {new Date(question.created_at).toLocaleString('en-IN')}
            </Typography>
          </Grid>
          {question.updated_at && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Last Updated
              </Typography>
              <Typography variant="body1">
                {new Date(question.updated_at).toLocaleString('en-IN')}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default QuestionView;
